import { useState, useCallback, useRef, useEffect } from 'react';
import { utterancesService } from '../services/utterances';
import { SyncOperation, SyncStatus } from '../types';

interface UseUtteranceSavingProps {
  audioFileId: string;
  onAllOperationsComplete?: () => void;
  onOperationComplete?: (operation: SyncOperation) => void;
}

interface UseUtteranceSavingReturn {
  syncStatus: SyncStatus;
  pendingOperationsCount: number;
  queueOperation: (operation: SyncOperation) => void;
}

export const useUtteranceSaving = (
  props: UseUtteranceSavingProps
): UseUtteranceSavingReturn => {
  const { audioFileId, onAllOperationsComplete, onOperationComplete } = props;
  const [pendingOperations, setPendingOperations] = useState<SyncOperation[]>(
    []
  );
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');
  const processingRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingOperationsRef = useRef<SyncOperation[]>([]);

  // Process individual operation
  const processOperation = useCallback(
    async (operation: SyncOperation): Promise<void> => {
      // Record when we're sending this to backend
      operation.sentAt = Date.now();

      switch (operation.type) {
        case 'utterance_speaker_change':
          try {
            await utterancesService.updateUtteranceSpeaker(
              audioFileId,
              operation.utteranceId,
              operation.data.newSpeakerId
            );
          } catch (error) {
            console.error(
              `❌ Failed to update speaker for utterance ${operation.utteranceId}:`,
              error
            );
            throw error;
          }
          break;

        case 'utterance_words_edit':
          try {
            await utterancesService.updateUtteranceWords(
              audioFileId,
              operation.utteranceId,
              operation.data.newEditorWords
            );
          } catch (error) {
            console.error(
              `❌ Failed to update utterance words for ${operation.utteranceId}:`,
              error
            );
            throw error;
          }
          break;

        case 'utterance_split':
          try {
            await utterancesService.splitUtterance(
              audioFileId,
              operation.utteranceId,
              operation.data.firstWordId,
              operation.data.newUtteranceId
            );
          } catch (error) {
            console.error(
              `❌ Failed to split utterance ${operation.utteranceId}:`,
              error
            );
            throw error;
          }
          break;

        case 'utterance_merge':
          try {
            // Call the backend API (backend automatically determines source as next utterance)
            await utterancesService.mergeUtterances(
              audioFileId,
              operation.data.targetUtteranceId
            );
          } catch (error) {
            console.error(`❌ Failed to merge utterances:`, error);
            throw error;
          }
          break;
      }
    },
    [audioFileId]
  );

  // Process the operations queue - ONE AT A TIME for sequential processing
  const processQueue = useCallback(async () => {
    if (processingRef.current || pendingOperationsRef.current.length === 0) {
      return;
    }

    processingRef.current = true;

    // Process only ONE operation at a time to ensure proper sequencing
    const currentOperation = pendingOperationsRef.current[0];

    try {
      await processOperation(currentOperation);

      // Notify completion callback
      if (onOperationComplete) {
        onOperationComplete(currentOperation);
      }

      // Remove the processed operation
      pendingOperationsRef.current = pendingOperationsRef.current.slice(1);
      setPendingOperations(pendingOperationsRef.current);

      // Update sync status
      const remainingOps = pendingOperationsRef.current.length;
      setSyncStatus(remainingOps > 0 ? 'pending' : 'synced');

      // Call completion callback when all operations are done
      if (remainingOps === 0 && onAllOperationsComplete) {
        onAllOperationsComplete();
      }

      // Continue processing if there are more operations
      // Small delay to avoid overwhelming the backend
      if (remainingOps > 0) {
        setTimeout(() => processQueue(), 100);
      }
    } catch (error) {
      console.error(
        `❌ Operation failed: ${currentOperation.type} for ${currentOperation.utteranceId}`,
        error
      );

      // Remove the failed operation to avoid blocking the queue
      pendingOperationsRef.current = pendingOperationsRef.current.slice(1);
      setPendingOperations(pendingOperationsRef.current);

      // Continue processing remaining operations
      if (pendingOperationsRef.current.length > 0) {
        setTimeout(() => processQueue(), 100);
      } else {
        setSyncStatus('error');
      }
    } finally {
      processingRef.current = false;
    }
  }, [onAllOperationsComplete, onOperationComplete, processOperation]);

  // Queue an operation for backend sync
  const queueOperation = useCallback(
    (operation: SyncOperation) => {
      setPendingOperations((prev) => {
        // Remove any existing operations of the same type for the same utterance
        // This implements a "latest wins" strategy for conflicting operations
        const filtered = prev.filter(
          (op) =>
            !(
              op.type === operation.type &&
              op.utteranceId === operation.utteranceId
            )
        );
        const newOps = [...filtered, operation];
        // Update the ref as well
        pendingOperationsRef.current = newOps;
        return newOps;
      });

      setSyncStatus('pending');

      // Smart batching: Only batch text edits
      // Speaker changes, split/merge operations should execute immediately
      const shouldBatch = operation.type === 'utterance_words_edit';

      if (shouldBatch) {
        // Debounce processing for batchable operations (1 second delay)
        // This allows multiple rapid changes to be accumulated and only the latest sent
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          processQueue();
        }, 1000); // 1 second debounce to batch operations and reduce backend load
      } else {
        // Process immediately for non-batchable operations (split/merge)
        // First, process any pending batched operations to ensure correct order
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          // Process batched operations immediately to avoid race conditions
          processQueue();
        }

        // Then schedule the structural operation with a small delay
        setTimeout(() => processQueue(), 50);
      }
    },
    [processQueue]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    syncStatus,
    pendingOperationsCount: pendingOperations.length,
    queueOperation,
  };
};
